import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Settings2 } from "lucide-react";
import { acceptAll, rejectAll, setConsent, getConsent } from "@/lib/consent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [mapsOn, setMapsOn] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      const existing = getConsent();
      if (!existing) {
        setVisible(true);
      } else {
        setMapsOn(!!existing.maps);
      }
    }, 600);
    return () => clearTimeout(t);
  }, []);

  const handleAccept = () => {
    acceptAll();
    setVisible(false);
  };

  const handleReject = () => {
    rejectAll();
    setVisible(false);
  };

  const handleSave = () => {
    setConsent({ maps: mapsOn });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie-Hinweis"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 lg:left-auto lg:right-6 lg:max-w-md z-[60] animate-fade-up"
    >
      <div className="relative bg-ink-700/95 backdrop-blur-xl border border-gold/40 shadow-[0_10px_40px_rgba(0,0,0,0.6)] p-6">
        <button
          data-testid="cookie-close"
          aria-label="Schließen"
          onClick={handleReject}
          className="absolute top-3 right-3 text-zinc-500 hover:text-gold transition-colors"
        >
          <X size={16} />
        </button>

        <div className="flex items-start gap-3 mb-3">
          <Cookie size={20} className="text-gold mt-0.5 flex-shrink-0" />
          <h3 className="font-display text-lg text-white">Cookies & Datenschutz</h3>
        </div>

        {!showSettings ? (
          <>
            <p className="text-sm text-zinc-300 leading-relaxed mb-5">
              Wir verwenden technisch notwendige Cookies. Mit Ihrer Zustimmung binden wir
              zusätzlich <strong className="text-white">Google Maps</strong> ein, um Ihnen
              unseren Standort interaktiv zu zeigen — dabei wird eine Verbindung zu Google
              hergestellt. Details in unserer{" "}
              <Link to="/datenschutz" className="text-gold underline hover:no-underline">
                Datenschutzerklärung
              </Link>
              .
            </p>

            <div className="flex flex-col gap-2">
              <button
                data-testid="cookie-accept-all"
                onClick={handleAccept}
                className="w-full px-5 py-3 bg-gold text-ink-900 text-sm font-medium uppercase tracking-wider hover:bg-gold-hover transition-colors"
              >
                Alle akzeptieren
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button
                  data-testid="cookie-reject"
                  onClick={handleReject}
                  className="px-5 py-3 border border-ink-500 text-zinc-200 text-sm font-medium uppercase tracking-wider hover:border-gold/50 hover:text-gold transition-colors"
                >
                  Nur notwendige
                </button>
                <button
                  data-testid="cookie-settings"
                  onClick={() => setShowSettings(true)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-gold/50 text-gold text-sm font-medium uppercase tracking-wider hover:bg-gold/10 transition-colors"
                >
                  <Settings2 size={14} /> Auswahl
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-zinc-400 leading-relaxed mb-4">
              Sie können einzelne Kategorien gezielt aktivieren.
            </p>

            <div className="space-y-3 mb-5">
              <div className="flex items-start justify-between gap-4 border border-ink-500 p-4">
                <div>
                  <p className="text-sm font-medium text-white">Notwendig</p>
                  <p className="text-xs text-zinc-400 mt-1">
                    Sicherheit & Funktion der Seite. Immer aktiv.
                  </p>
                </div>
                <span className="text-xs text-zinc-500 uppercase tracking-wider mt-1">
                  Aktiv
                </span>
              </div>

              <div className="flex items-start justify-between gap-4 border border-ink-500 p-4">
                <div>
                  <p className="text-sm font-medium text-white">Google Maps</p>
                  <p className="text-xs text-zinc-400 mt-1">
                    Standort-Karte. Bei Aktivierung wird eine Verbindung zu Google aufgebaut.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer mt-1">
                  <input
                    data-testid="cookie-toggle-maps"
                    type="checkbox"
                    className="sr-only peer"
                    checked={mapsOn}
                    onChange={(e) => setMapsOn(e.target.checked)}
                  />
                  <div className="w-10 h-5 bg-ink-500 peer-focus:outline-none rounded-full peer-checked:bg-gold transition-colors pointer-events-none" />
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5 pointer-events-none" />
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="cookie-back"
                onClick={() => setShowSettings(false)}
                className="px-5 py-3 border border-ink-500 text-zinc-200 text-sm font-medium uppercase tracking-wider hover:border-gold/50 hover:text-gold transition-colors"
              >
                Zurück
              </button>
              <button
                data-testid="cookie-save"
                onClick={handleSave}
                className="px-5 py-3 bg-gold text-ink-900 text-sm font-medium uppercase tracking-wider hover:bg-gold-hover transition-colors"
              >
                Speichern
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
